<template>
  <div class="flex items-center justify-between w-full px-3 py-2">
    <a-tooltip
      :title="row.sender_email || 'No email available'"
      placement="top"
      :disabled="!row.sender_email"
    >
      <div class="flex items-center gap-2 flex-1 min-w-0 cursor-pointer">
        <ProfileIcon
          :data="senderData"
          :size="32"
          :font="12"
          class="flex-shrink-0"
        />
        <div class="flex flex-col min-w-0 flex-1">
          <span class="text-sm font-medium text_color truncate">
            {{ row.sender_name || "Unknown Sender" }}
          </span>
        </div>
      </div>
    </a-tooltip>

    <a-tooltip
      v-if="getWarningMessage()"
      :title="getWarningMessage()"
      placement="topRight"
    >
      <div class="warning_icon_container">
        <ThemeImage
          :icon="imageNames.WARNING"
          alt="warning"
          class="w-4 h-4 flex-shrink-0"
        />
      </div>
    </a-tooltip>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { useStore } from "vuex";
import ProfileIcon from "@/src/components/ProfileIcon.vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import {
  WARNING_MESSAGES,
  stepTypeConstants,
} from "@/src/constants/sequenceConstants.js";

const props = defineProps({
  row: {
    type: Object,
    required: true,
  },
  sequenceSteps: {
    type: Array,
    default: () => [],
  },
});

const store = useStore();
const storeUsers = computed(() => store.getters["users/users"] || []);

const senderData = computed(() => {
  const name = props.row.sender_name || "Unknown Sender";
  const lowerName = name.toLowerCase();
  const found = storeUsers.value.find((u) => u.name?.toLowerCase() === lowerName);
  return found || { name };
});

const hasSenderEmailConnected = computed(() => {
  return props.row.sender_email_connected === true;
});

const hasSenderLinkedInConnected = computed(() => {
  return props.row.sender_linkedin_connected === true;
});

const hasEmailSteps = computed(() => {
  if (!props.sequenceSteps || props.sequenceSteps.length === 0) return false;
  return props.sequenceSteps.some(
    (step) => step.step_type === stepTypeConstants.EMAIL,
  );
});

const hasLinkedInSteps = computed(() => {
  if (!props.sequenceSteps || props.sequenceSteps.length === 0) return false;
  return props.sequenceSteps.some(
    (step) =>
      step.step_type && step.step_type.startsWith(stepTypeConstants.LINKEDIN),
  );
});

const getWarningMessage = () => {
  const emailConnected = hasSenderEmailConnected.value;
  const linkedinConnected = hasSenderLinkedInConnected.value;
  const hasEmailStep = hasEmailSteps.value;
  const hasLinkedInStep = hasLinkedInSteps.value;

  if (props.sequenceSteps === null || props.sequenceSteps === undefined) {
    return null;
  }

  const noSteps = props.sequenceSteps.length === 0;

  if (noSteps) {
    if (!emailConnected && !linkedinConnected) {
      return WARNING_MESSAGES.SENDER.BOTH_DISCONNECTED;
    }
    if (emailConnected && !linkedinConnected) {
      return WARNING_MESSAGES.SENDER.EMAIL_ONLY_CONNECTED;
    }
    if (!emailConnected && linkedinConnected) {
      return WARNING_MESSAGES.SENDER.LINKEDIN_ONLY_CONNECTED;
    }
    return null;
  }

  if (hasEmailStep && !hasLinkedInStep) {
    if (!emailConnected) {
      return WARNING_MESSAGES.SENDER.EMAIL_DISCONNECTED;
    }
    return null;
  }

  if (!hasEmailStep && hasLinkedInStep) {
    if (!linkedinConnected) {
      return WARNING_MESSAGES.SENDER.LINKEDIN_DISCONNECTED;
    }
    return null;
  }

  if (hasEmailStep && hasLinkedInStep) {
    if (!emailConnected && !linkedinConnected) {
      return WARNING_MESSAGES.SENDER.BOTH_DISCONNECTED;
    }
    if (emailConnected && !linkedinConnected) {
      return WARNING_MESSAGES.SENDER.EMAIL_ONLY_CONNECTED;
    }
    if (!emailConnected && linkedinConnected) {
      return WARNING_MESSAGES.SENDER.LINKEDIN_ONLY_CONNECTED;
    }
    return null;
  }

  return null;
};
</script>

<style scoped>
.warning_icon_container {
  border-radius: 24px;
  background: var(--warning-icon-bg);
  padding: 4px;
  margin-left: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
